import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment-timezone'
import { useHistory } from 'react-router-dom'
import { useErrorService, useNotificationService } from '@abroad/components'
import API from '../../utils/API'
import DateOverrideModal from './DateOverrideModal'
import DateOverridesSection from './DateOverridesSection'
import WeeklyAvailabilitySchedule from './WeeklyAvailabilitySchedule'
import { PlusIcon } from '../assetsComponents/common'

const WEEK_DAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]
const createEmptyRange = () => ({
  start: '',
  end: '',
})

const defaultAvailability = WEEK_DAYS.map((day) => ({
  day,
  isActive: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].includes(
    day,
  ),
  timeRanges: [createEmptyRange()],
}))

const cloneAvailability = (items = []) =>
  items.map((item) => ({
    ...item,
    timeRanges: item.timeRanges?.length
      ? item.timeRanges.map((range) => ({ ...range }))
      : [createEmptyRange()],
  }))

const cloneOverrides = (items = []) =>
  items.map((item) => ({
    ...item,
    timeRanges: item.timeRanges?.length
      ? item.timeRanges.map((range) => ({ ...range }))
      : [createEmptyRange()],
  }))

const AvailabilityTab = ({ timezone }) => {
  const history = useHistory()
  const [availability, setAvailability] = useState(() =>
    cloneAvailability(defaultAvailability),
  )
  const [overrides, setOverrides] = useState([])
  const [initialAvailability, setInitialAvailability] = useState(() =>
    cloneAvailability(defaultAvailability),
  )
  const [initialOverrides, setInitialOverrides] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [showDateOverrideModal, setShowDateOverrideModal] = useState(false)
  const [selectedDates, setSelectedDates] = useState([])
  const [overrideTimeRanges, setOverrideTimeRanges] = useState([
    createEmptyRange(),
  ])
  const [isOverrideUnavailable, setIsOverrideUnavailable] = useState(false)
  const [editingOverrideIndex, setEditingOverrideIndex] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isRemovingOverride, setIsRemovingOverride] = useState(false)

  const Error = useErrorService()
  const Notification = useNotificationService()

  const fetchAvailability = async (isInitialLoad = false) => {
    if (isInitialLoad) {
      setIsLoading(true)
    }
    try {
      const { data } = await API.AdminAPI.scheduling.getCalendarLimit()
      const hasAvailabilityData =
        data?.availability &&
        Array.isArray(data.availability) &&
        data.availability.length > 0
      const normalizedAvailability = normalizeAvailabilityResponse(
        data?.availability,
        !hasAvailabilityData,
      )
      const normalizedOverrides = normalizeOverridesResponse(data?.overrides)

      setAvailability(normalizedAvailability)
      setInitialAvailability(cloneAvailability(normalizedAvailability))

      setOverrides(normalizedOverrides)
      setInitialOverrides(cloneOverrides(normalizedOverrides))
    } catch (error) {
      Error.showError(error)
      if (isInitialLoad) {
        setAvailability(cloneAvailability(defaultAvailability))
        setOverrides([])
      }
    } finally {
      if (isInitialLoad) {
        setIsLoading(false)
      }
    }
  }

  useEffect(() => {
    fetchAvailability(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const normalizeAvailabilityResponse = (items = [], isFirstLoad = false) => {
    const entries = Array.isArray(items) ? items : []
    const itemsMap = entries.reduce((acc, item) => {
      acc[item.day] = item
      return acc
    }, {})

    // Check if API returned any data at all
    const hasApiData = entries.length > 0
    // If first load or no API data, default weekdays to active
    const shouldUseDefaults = isFirstLoad || !hasApiData

    return WEEK_DAYS.map((day) => {
      const found = itemsMap[day] || {}
      const hasRanges =
        Array.isArray(found.timeRanges) && found.timeRanges.length > 0

      // If API has data for this day, use it. Otherwise, default weekdays to active
      let isActive
      if (found.isActive !== undefined) {
        // API explicitly set this day's status
        isActive = Boolean(found.isActive)
      } else if (shouldUseDefaults) {
        // First load or no API data, use default: weekdays active
        isActive = [
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
        ].includes(day)
      } else {
        // API data exists but this day wasn't included, default to inactive
        isActive = false
      }

      return {
        day,
        isActive,
        timeRanges: hasRanges
          ? found.timeRanges.map((range) => ({
              start: range.start || '',
              end: range.end || '',
            }))
          : [createEmptyRange()],
      }
    })
  }

  const normalizeOverridesResponse = (items = []) => {
    if (!Array.isArray(items)) return []
    return items.map((item) => {
      // Check if item is in new format (has start, end, timezone)
      if (item.start && item.end && item.timezone !== undefined) {
        // New format: pass through as-is, preserving ID
        return {
          _id: item._id,
          start: item.start,
          end: item.end,
          timezone: item.timezone,
          isActive: item.isActive !== false,
        }
      }
      // Old format: convert to old structure for backward compatibility
      const hasRanges =
        Array.isArray(item.timeRanges) && item.timeRanges.length > 0
      return {
        _id: item._id,
        startDate: item.startDate
          ? moment(item.startDate).format('YYYY-MM-DD')
          : '',
        endDate: item.endDate ? moment(item.endDate).format('YYYY-MM-DD') : '',
        isActive: Boolean(item.isActive),
        timeRanges: hasRanges
          ? item.timeRanges.map((range) => ({
              start: range.start || '',
              end: range.end || '',
            }))
          : [createEmptyRange()],
      }
    })
  }

  const handleToggleDay = (dayName) => {
    setAvailability((prev) =>
      prev.map((day) =>
        day.day === dayName
          ? {
              ...day,
              isActive: !day.isActive,
              timeRanges: day.isActive
                ? day.timeRanges
                : day.timeRanges.length
                ? day.timeRanges
                : [createEmptyRange()],
            }
          : day,
      ),
    )
  }

  const handleAddTimeRange = (dayName) => {
    setAvailability((prev) =>
      prev.map((day) => {
        if (day.day !== dayName) return day
        return {
          ...day,
          timeRanges: [...day.timeRanges, createEmptyRange()],
        }
      }),
    )
  }

  const handleRemoveTimeRange = (dayName, rangeIndex) => {
    setAvailability((prev) =>
      prev.map((day) => {
        if (day.day !== dayName) return day
        const updatedRanges = day.timeRanges.filter(
          (_, index) => index !== rangeIndex,
        )
        return {
          ...day,
          timeRanges: updatedRanges.length
            ? updatedRanges
            : [createEmptyRange()],
        }
      }),
    )
  }

  const handleAvailabilityChange = (updatedAvailability) => {
    setAvailability(updatedAvailability)
  }

  const handleAddOverride = () => {
    setEditingOverrideIndex(null)
    setSelectedDates([])
    setOverrideTimeRanges([createEmptyRange()])
    setIsOverrideUnavailable(false)
    setShowDateOverrideModal(true)
  }

  const handleRemoveOverride = async (override) => {
    setIsRemovingOverride(true)
    try {
      // Get the override ID
      const overrideId = override._id || override.id

      if (!overrideId) {
        Notification.showNotification(
          'Unable to remove override: missing override ID.',
          'danger',
        )
        return
      }

      // Call API to remove the override
      const { data } = await API.AdminAPI.scheduling.removeDateOverride(
        overrideId,
      )
      if (data?.status) {
        Notification.showNotification(
          'Date override removed successfully.',
          'success',
        )
      }

      // Refresh the availability and overrides from the server
      await fetchAvailability(false)
    } catch (error) {
      Error.showError(error)
    } finally {
      setIsRemovingOverride(false)
    }
  }

  const handleDatesChange = (dates) => {
    setSelectedDates(dates)

    // If dates are selected, check if any of them already have overrides
    if (dates && dates.length > 0) {
      // Find all overrides that match the selected dates
      const matchingOverrides = []
      const dateOverrideMap = new Map()

      dates.forEach((date) => {
        const dateMoment = moment(date)
        const dateStr = dateMoment.format('YYYY-MM-DD')

        // Find overrides for this specific date (handle both old and new formats)
        const matchingOverride = overrides.find((override) => {
          // New format: check if start timestamp matches the date using timezone-aware comparison
          if (
            override.start &&
            override.end &&
            override.timezone !== undefined
          ) {
            // Use timezone-aware date comparison to match what we do in processing
            const overrideDate = moment
              .utc(override.start)
              .tz(override.timezone)
              .format('YYYY-MM-DD')
            return overrideDate === dateStr
          }
          // Old format: check date range
          if (override.startDate && override.endDate) {
            const overrideStart = moment(override.startDate)
            const overrideEnd = moment(override.endDate)
            return (
              dateMoment.isSameOrAfter(overrideStart, 'day') &&
              dateMoment.isSameOrBefore(overrideEnd, 'day')
            )
          }
          return false
        })

        if (matchingOverride) {
          dateOverrideMap.set(dateStr, matchingOverride)
          if (!matchingOverrides.includes(matchingOverride)) {
            matchingOverrides.push(matchingOverride)
          }
        }
      })

      // Group overrides by date and extract time ranges
      const dateTimeRangesMap = new Map()
      let hasUnavailable = false
      const allMatchingOverrideIndices = []

      // Collect all overrides for the selected date
      // Normalize selected date to YYYY-MM-DD format (handle both ISO strings and date objects)
      const selectedDateMoment = dates[0] ? moment(dates[0]) : null
      const selectedDateStr = selectedDateMoment
        ? selectedDateMoment.format('YYYY-MM-DD')
        : null

      if (selectedDateStr) {
        overrides.forEach((override, index) => {
          let overrideDateStr = null
          let isUnavailableOverride = false
          let timeRanges = []

          // New format
          if (
            override.start &&
            override.end &&
            override.timezone !== undefined
          ) {
            // Get the date in the override's timezone (not UTC) for comparison
            // This ensures we match the date as the user sees it
            const startInTimezone = moment
              .utc(override.start)
              .tz(override.timezone)
            overrideDateStr = startInTimezone.format('YYYY-MM-DD')
            isUnavailableOverride = override.isActive === false

            if (overrideDateStr === selectedDateStr) {
              // Track this override index
              allMatchingOverrideIndices.push(index)

              if (isUnavailableOverride) {
                hasUnavailable = true
              } else {
                // Convert UTC to the timezone specified in override
                const endInTimezone = moment
                  .utc(override.end)
                  .tz(override.timezone)

                // Round minutes to nearest 15-minute increment (00, 15, 30, 45)
                // This is required because TimeSelection only accepts these increments
                const roundToQuarter = (minute) => {
                  return Math.round(minute / 15) * 15
                }

                const startHour = startInTimezone.hour()
                const startMinute = roundToQuarter(startInTimezone.minute())
                const endHour = endInTimezone.hour()
                const endMinute = roundToQuarter(endInTimezone.minute())

                // Format as HH:mm
                const startTime = `${startHour
                  .toString()
                  .padStart(2, '0')}:${startMinute.toString().padStart(2, '0')}`
                const endTime = `${endHour
                  .toString()
                  .padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}`

                // Only add if both start and end are valid
                if (startTime && endTime) {
                  timeRanges.push({
                    start: startTime,
                    end: endTime,
                  })
                }
              }
            }
          }
          // Old format
          else if (override.startDate && override.endDate) {
            overrideDateStr = moment(override.startDate).format('YYYY-MM-DD')
            isUnavailableOverride = !override.isActive

            if (overrideDateStr === selectedDateStr) {
              // Track this override index
              allMatchingOverrideIndices.push(index)

              if (isUnavailableOverride) {
                hasUnavailable = true
              } else if (
                override.timeRanges &&
                override.timeRanges.length > 0
              ) {
                timeRanges = override.timeRanges.filter(
                  (range) => range.start && range.end,
                )
              }
            }
          }

          if (overrideDateStr === selectedDateStr && timeRanges.length > 0) {
            if (!dateTimeRangesMap.has(overrideDateStr)) {
              dateTimeRangesMap.set(overrideDateStr, [])
            }
            dateTimeRangesMap.get(overrideDateStr).push(...timeRanges)
          }
        })
      }

      // If we found overrides for the selected date
      const allTimeRanges = Array.from(dateTimeRangesMap.values()).flat()

      // Priority: If there are time ranges, show them (even if there's also an unavailable override)
      // Only show unavailable if there are NO time ranges
      if (allTimeRanges.length > 0) {
        // Date has time ranges - show them
        setOverrideTimeRanges(allTimeRanges)
        setIsOverrideUnavailable(false)
        // Set editing index to first matching override (we'll remove all of them when saving)
        setEditingOverrideIndex(
          allMatchingOverrideIndices.length > 0
            ? allMatchingOverrideIndices[0]
            : null,
        )
      } else if (hasUnavailable) {
        // Date is unavailable and has no time ranges
        setOverrideTimeRanges([createEmptyRange()])
        setIsOverrideUnavailable(true)
        setEditingOverrideIndex(
          allMatchingOverrideIndices.length > 0
            ? allMatchingOverrideIndices[0]
            : null,
        )
      } else {
        // No matching overrides for selected date
        setEditingOverrideIndex(null)
        setOverrideTimeRanges([createEmptyRange()])
        setIsOverrideUnavailable(false)
      }
    } else {
      // No dates selected, reset to defaults and clear editing index
      setEditingOverrideIndex(null)
      setOverrideTimeRanges([createEmptyRange()])
      setIsOverrideUnavailable(false)
    }
  }

  const handleAvailabilitySaveSuccess = (data) => {
    // Update state from API response
    // API returns only availability, so preserve existing overrides
    if (data?.availability) {
      const normalizedAvailability = normalizeAvailabilityResponse(
        data?.availability,
      )
      setAvailability(normalizedAvailability)
      setInitialAvailability(cloneAvailability(normalizedAvailability))
    }
    // If API also returns overrides, update them too
    if (data?.overrides !== undefined) {
      const normalizedOverrides = normalizeOverridesResponse(data?.overrides)
      setOverrides(normalizedOverrides)
      setInitialOverrides(cloneOverrides(normalizedOverrides))
    }
  }

  const handleOverrideSaveSuccess = async (data) => {
    // After updating overrides, fetch latest data from server
    // This ensures we have the correct _id values and latest state
    await fetchAvailability(false)

    // Reset modal state
    setShowDateOverrideModal(false)
    setEditingOverrideIndex(null)
    setSelectedDates([])
    setOverrideTimeRanges([createEmptyRange()])
    setIsOverrideUnavailable(false)
  }

  const handleCloseDateOverrideModal = () => {
    setShowDateOverrideModal(false)
    setEditingOverrideIndex(null)
    setSelectedDates([])
    setOverrideTimeRanges([createEmptyRange()])
    setIsOverrideUnavailable(false)
  }

  const handleAddOverrideTimeRange = () => {
    setOverrideTimeRanges((prev) => [...prev, createEmptyRange()])
  }

  const handleRemoveOverrideTimeRange = (rangeIndex) => {
    setOverrideTimeRanges((prev) => {
      const updatedRanges = prev.filter((_, index) => index !== rangeIndex)
      return updatedRanges.length ? updatedRanges : [createEmptyRange()]
    })
  }

  const handleOverrideTimeSelectionUpdate = (rangeIndex, start, end) => {
    setOverrideTimeRanges((prev) =>
      prev.map((range, idx) =>
        idx === rangeIndex
          ? {
              ...range,
              start: start || '',
              end: end || '',
            }
          : range,
      ),
    )
  }

  const handleOverrideUnavailableToggle = (event) => {
    const checked = event && event.target ? event.target.checked : false
    setIsOverrideUnavailable(checked)
  }

  const handleCancel = () => {
    setAvailability(cloneAvailability(initialAvailability))
    setOverrides(cloneOverrides(initialOverrides))
  }

  const handleTimeSelectionUpdate = (dayName, rangeIndex, start, end) => {
    setAvailability((prev) =>
      prev.map((day) => {
        if (day.day !== dayName) return day
        const updatedRanges = day.timeRanges.map((range, idx) =>
          idx === rangeIndex
            ? {
                ...range,
                start: start || '',
                end: end || '',
              }
            : range,
        )
        return {
          ...day,
          timeRanges: updatedRanges.length
            ? updatedRanges
            : [createEmptyRange()],
        }
      }),
    )
  }

  const buildAvailabilityPayload = () => {
    return availability.map((day) => {
      const payload = {
        day: day.day,
        isActive: day.isActive,
      }
      // Only include timeRanges if isActive is true, otherwise send empty array
      if (day.isActive) {
        payload.timeRanges = day.timeRanges
          .filter((range) => range.start && range.end)
          .map((range) => ({
            start: range.start,
            end: range.end,
          }))
      } else {
        payload.timeRanges = []
      }
      return payload
    })
  }

  const hasAnyTimeRange = availability.some(
    (day) =>
      day.isActive &&
      Array.isArray(day.timeRanges) &&
      day.timeRanges.some(
        (range) =>
          range.start && range.end && range.start.trim() && range.end.trim(),
      ),
  )

  const areAllDaysInactive = availability.every((day) => !day.isActive)

  const handleSave = async () => {
    // Validate that if there are any active days, at least one must have a time range set
    // Allow saving if all days are inactive
    if (!areAllDaysInactive && !hasAnyTimeRange) {
      return Notification.showNotification(
        'Please set at least one time range for any active day.',
        'danger',
      )
    }

    setIsSaving(true)
    try {
      const availabilityPayload = buildAvailabilityPayload()

      // Send only availability payload
      const payload = {
        availability: availabilityPayload,
      }

      const { data } = await API.AdminAPI.scheduling.updateCalendarAvailability(
        payload,
      )

      // Update state from API response
      handleAvailabilitySaveSuccess(data)

      Notification.showNotification(
        'Availability updated successfully.',
        'success',
      )
    } catch (error) {
      Error.showError(error)
    } finally {
      setIsSaving(false)
    }
  }

  const hasUnsavedAvailabilityChanges = () => {
    if (availability.length !== initialAvailability.length) return true

    // Create a map of initial availability by day name for easier lookup
    const initialAvailabilityMap = initialAvailability.reduce((acc, day) => {
      acc[day.day] = day
      return acc
    }, {})

    return availability.some((day) => {
      const initialDay = initialAvailabilityMap[day.day]
      if (!initialDay) return true

      // Check if isActive changed
      if (day.isActive !== initialDay.isActive) return true

      // Check if timeRanges changed
      const currentRanges = day.timeRanges || []
      const initialRanges = initialDay.timeRanges || []

      if (currentRanges.length !== initialRanges.length) return true

      return currentRanges.some((range, rangeIndex) => {
        const initialRange = initialRanges[rangeIndex]
        if (!initialRange) return true
        return (
          (range.start || '') !== (initialRange.start || '') ||
          (range.end || '') !== (initialRange.end || '')
        )
      })
    })
  }

  const shouldShowTooltip =
    areAllDaysInactive || hasUnsavedAvailabilityChanges()

  return (
    <div className='availability-layout'>
      <WeeklyAvailabilitySchedule
        availability={availability}
        onToggleDay={handleToggleDay}
        onTimeSelectionUpdate={handleTimeSelectionUpdate}
        onAddTimeRange={handleAddTimeRange}
        onRemoveTimeRange={handleRemoveTimeRange}
        onAvailabilityChange={handleAvailabilityChange}
        isLoading={isLoading}
        onCancel={handleCancel}
        onSave={handleSave}
        isSaving={isSaving}
        hasAnyTimeRange={hasAnyTimeRange}
      />

      <DateOverridesSection
        overrides={overrides}
        onAddOverride={handleAddOverride}
        onRemoveOverride={handleRemoveOverride}
        isLoading={isLoading || isRemovingOverride || isSaving}
        shouldShowTooltip={shouldShowTooltip}
      />

      <DateOverrideModal
        show={showDateOverrideModal}
        onHide={handleCloseDateOverrideModal}
        selectedDates={selectedDates}
        onDatesChange={handleDatesChange}
        timeRanges={overrideTimeRanges}
        onAddTimeRange={handleAddOverrideTimeRange}
        onRemoveTimeRange={handleRemoveOverrideTimeRange}
        onTimeSelectionUpdate={handleOverrideTimeSelectionUpdate}
        isUnavailable={isOverrideUnavailable}
        onUnavailableToggle={handleOverrideUnavailableToggle}
        onSaveSuccess={handleOverrideSaveSuccess}
        currentOverrides={overrides}
        editingOverrideIndex={editingOverrideIndex}
        timezone={timezone}
        onConflictContinue={(dateStr, events) => {
          // Navigate to booking tab with highlight dates in state
          history.push('/coach/scheduling/booking', {
            highlightDate: dateStr,
            highlightEventIds: events.map((e) => e._id || e.id),
          })
        }}
        AddIcon={PlusIcon}
      />
    </div>
  )
}

AvailabilityTab.propTypes = {
  timezone: PropTypes.string,
}

export default AvailabilityTab
